/**
 * Les échéances d'un dossier, en calendrier (iCalendar, RFC 5545).
 *
 * UN FICHIER QUE TOUS LES AGENDAS LISENT. Google Agenda, Apple Calendrier et
 * Outlook importent un .ics, et s'y abonnent quand il est servi à une adresse
 * fixe : c'est ce que fait la route du compte (app/api/tools/agenda/[jeton]).
 * Sans compte, la page le fait télécharger, construit dans le navigateur.
 *
 * DES JOURNÉES ENTIÈRES, AVEC UN RAPPEL LA VEILLE. Une échéance fiscale tombe
 * un jour, pas à une heure : l'événement couvre la journée, et l'alarme sonne
 * la veille au matin, le seul moment où un rappel sert encore.
 *
 * L'identifiant de chaque événement ne dépend que de l'échéance et de sa date :
 * un agenda abonné met à jour l'événement au lieu d'en créer un second quand
 * le fichier est relu.
 */

import { libelleEcheance } from "./dossierTextes.js";

const NOM = {
    fr: "Mes échéances de solopreneur (Believemy)",
    en: "My solopreneur deadlines (Believemy)",
};
const RAPPEL = { fr: "Demain", en: "Tomorrow" };
const DETAIL = {
    fr: "Calculé depuis votre dossier sur believemy.com. Le détail et le calcul : {url}",
    en: "Worked out from your profile on believemy.com. Details and calculation: {url}",
};

/** Échappe le texte d'une propriété iCalendar. */
function echapper(texte) {
    return String(texte).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/** Replie une ligne à 75 octets, comme l'exige la norme. */
function plier(ligne) {
    const octets = new TextEncoder().encode(ligne);
    if (octets.length <= 75) return ligne;
    const morceaux = [];
    let courant = "";
    let taille = 0;
    for (const car of ligne) {
        const t = new TextEncoder().encode(car).length;
        if (taille + t > (morceaux.length ? 74 : 75)) {
            morceaux.push(courant);
            courant = "";
            taille = 0;
        }
        courant += car;
        taille += t;
    }
    morceaux.push(courant);
    return morceaux.join("\r\n ");
}

function jourIcs(iso) {
    return iso.slice(0, 10).replace(/-/g, "");
}

function lendemain(iso) {
    const [a, m, j] = iso.slice(0, 10).split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j + 1));
    return d.toISOString().slice(0, 10).replace(/-/g, "");
}

function horodatage(date) {
    return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/**
 * @param echeances [{ cle, date (AAAA-MM-JJ), famille, outil }]
 * @param lienOutil (cleOutil) => URL absolue de l'outil, ou null
 */
export function calendrierIcs(echeances, { lang = "fr", lienOutil = () => null, maintenant = new Date() } = {}) {
    const l = lang === "en" ? "en" : "fr";
    const lignes = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Believemy//Outils//FR",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        `X-WR-CALNAME:${echapper(NOM[l])}`,
        "X-WR-TIMEZONE:Europe/Paris",
        // Les agendas abonnés relisent le fichier une fois par jour.
        "REFRESH-INTERVAL;VALUE=DURATION:P1D",
        "X-PUBLISHED-TTL:P1D",
    ];
    for (const e of echeances) {
        const titre = libelleEcheance(e.cle, e.famille, l);
        const url = lienOutil(e.outil);
        lignes.push(
            "BEGIN:VEVENT",
            `UID:${e.cle}-${jourIcs(e.date)}@believemy.com`,
            `DTSTAMP:${horodatage(maintenant)}`,
            `DTSTART;VALUE=DATE:${jourIcs(e.date)}`,
            `DTEND;VALUE=DATE:${lendemain(e.date)}`,
            `SUMMARY:${echapper(titre)}`,
            ...(url ? [`DESCRIPTION:${echapper(DETAIL[l].replace("{url}", url))}`, `URL:${url}`] : []),
            "TRANSP:TRANSPARENT",
            "BEGIN:VALARM",
            "ACTION:DISPLAY",
            `DESCRIPTION:${echapper(`${RAPPEL[l]} : ${titre}`)}`,
            // La veille à 9 heures : quinze heures avant minuit.
            "TRIGGER:-PT15H",
            "END:VALARM",
            "END:VEVENT",
        );
    }
    lignes.push("END:VCALENDAR");
    return lignes.map(plier).join("\r\n") + "\r\n";
}
